import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { isUserAdmin } from '../utils/adminUtils';
import {
  ENTRY_TYPES,
  ENTRY_TYPE_LABELS,
  createManualAccountingEntry,
  createPurchaseEntryWithPdf,
  deleteAccountingEntry,
  openAccountingJournalPrint,
  subscribeToAccountingEntries,
  updateAccountingEntryDate
} from '../utils/accountingUtils';

const todayIso = () => new Date().toISOString().slice(0, 10);

const emptyManual = () => ({
  type: ENTRY_TYPES.OTHER,
  date: todayIso(),
  documentNumber: '',
  description: '',
  amountEur: '',
  counterpartyName: '',
  clientEmail: '',
  clientName: ''
});

const emptyPurchase = () => ({
  date: todayIso(),
  documentNumber: '',
  description: '',
  amountEur: '',
  counterpartyName: '',
  paidImmediately: true,
  file: null
});

export default function AdminApskaitaPage() {
  const { currentUser } = useAuth();
  const isAdminUser = isUserAdmin(currentUser);

  const [entries, setEntries] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterFrom, setFilterFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [filterTo, setFilterTo] = useState(todayIso());

  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState(emptyManual);
  const [manualSaving, setManualSaving] = useState(false);

  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState(emptyPurchase);
  const [purchaseSaving, setPurchaseSaving] = useState(false);

  const [editDateEntry, setEditDateEntry] = useState(null);
  const [editDateValue, setEditDateValue] = useState('');
  const [editDateSaving, setEditDateSaving] = useState(false);

  useEffect(() => {
    if (!isAdminUser) return undefined;
    return subscribeToAccountingEntries(setEntries);
  }, [isAdminUser]);

  const filtered = useMemo(() => {
    return (entries || []).filter((e) => {
      if (filterFrom && e.date < filterFrom) return false;
      if (filterTo && e.date > filterTo) return false;
      return true;
    });
  }, [entries, filterFrom, filterTo]);

  if (!currentUser || !isAdminUser) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Tik administratorius gali pasiekti apskaitos žurnalą.
        </Typography>
      </Container>
    );
  }

  const handleManualSave = async () => {
    setManualSaving(true);
    setError('');
    try {
      const type = manualForm.type;
      const needsClient = [
        ENTRY_TYPES.SALE,
        ENTRY_TYPES.PAYMENT_RECEIVED,
        ENTRY_TYPES.ADVANCE_RECEIVED,
        ENTRY_TYPES.ADVANCE_APPLIED
      ].includes(type);

      await createManualAccountingEntry({
        type,
        date: manualForm.date,
        documentNumber: manualForm.documentNumber,
        description: manualForm.description,
        amountEur: Number(manualForm.amountEur),
        counterpartyName: manualForm.counterpartyName || manualForm.clientName,
        clientName: manualForm.clientName || manualForm.counterpartyName,
        clientEmail: needsClient ? manualForm.clientEmail : '',
        source: 'manual'
      });
      setManualOpen(false);
      setManualForm(emptyManual());
      setSuccess('Įrašas pridėtas į žurnalą.');
    } catch (err) {
      setError(err.message || 'Nepavyko išsaugoti įrašo.');
    } finally {
      setManualSaving(false);
    }
  };

  const handlePurchaseSave = async () => {
    setPurchaseSaving(true);
    setError('');
    try {
      await createPurchaseEntryWithPdf({
        date: purchaseForm.date,
        documentNumber: purchaseForm.documentNumber,
        description: purchaseForm.description,
        amountEur: Number(purchaseForm.amountEur),
        counterpartyName: purchaseForm.counterpartyName,
        paidImmediately: purchaseForm.paidImmediately,
        file: purchaseForm.file
      });
      setPurchaseOpen(false);
      setPurchaseForm(emptyPurchase());
      setSuccess('Pirkimo įrašas išsaugotas.');
    } catch (err) {
      setError(err.message || 'Nepavyko išsaugoti pirkimo.');
    } finally {
      setPurchaseSaving(false);
    }
  };

  const handleSaveEditDate = async () => {
    if (!editDateEntry) return;
    setEditDateSaving(true);
    setError('');
    try {
      await updateAccountingEntryDate(editDateEntry.id, editDateValue);
      setEditDateEntry(null);
      setSuccess('Data atnaujinta.');
    } catch (err) {
      setError(err.message || 'Nepavyko atnaujinti datos.');
    } finally {
      setEditDateSaving(false);
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(`Ištrinti žurnalo įrašą ${entry.documentNumber || entry.id}? Sąskaitos faktūros nebus liečiamos.`)) {
      return;
    }
    try {
      await deleteAccountingEntry(entry.id);
      setSuccess('Žurnalo įrašas ištrintas (SF išsaugotos).');
    } catch (err) {
      setError(err.message || 'Nepavyko ištrinti.');
    }
  };

  const handlePrint = () => {
    try {
      openAccountingJournalPrint(filtered, {
        dateFrom: filterFrom,
        dateTo: filterTo,
        title: 'Apskaitos žurnalas (MB)'
      });
    } catch (err) {
      setError(err.message || 'Nepavyko atidaryti PDF peržiūros.');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, letterSpacing: '-0.02em' }}>
          Apskaitos žurnalas
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Dvigubo įrašo žurnalas (ne PVM). SF ir mokėjimai įrašomi automatiškai; pirkimus ir kitus įrašus galite suvesti ranka.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap">
            <TextField
              label="Nuo"
              type="date"
              size="small"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Iki"
              type="date"
              size="small"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="outlined" onClick={handlePrint}>PDF / spausdinti laikotarpį</Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="outlined" onClick={() => { setManualForm(emptyManual()); setManualOpen(true); }}>
              Rankinis įrašas
            </Button>
            <Button variant="outlined" onClick={() => { setPurchaseForm(emptyPurchase()); setPurchaseOpen(true); }}>
              Pirkimas + PDF
            </Button>
          </Stack>
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Dok. nr.</TableCell>
                <TableCell>Tipas</TableCell>
                <TableCell>Kontrahentas</TableCell>
                <TableCell>Aprašymas</TableCell>
                <TableCell>Debetas</TableCell>
                <TableCell>Kreditas</TableCell>
                <TableCell align="right">Suma</TableCell>
                <TableCell align="right">Veiksmai</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary">Nėra įrašų šiame laikotarpyje.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell>{e.date}</TableCell>
                  <TableCell>{e.documentNumber || '—'}</TableCell>
                  <TableCell>{e.typeLabel || ENTRY_TYPE_LABELS[e.type] || e.type}</TableCell>
                  <TableCell>{e.counterpartyName || e.clientName || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>{e.description}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.debitAccount}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.creditAccount}</TableCell>
                  <TableCell align="right">{Number(e.amountEur || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {e.pdfUrl && (
                        <Button size="small" href={e.pdfUrl} target="_blank" rel="noopener noreferrer">PDF</Button>
                      )}
                      <Button
                        size="small"
                        onClick={() => {
                          setEditDateEntry(e);
                          setEditDateValue(e.date || todayIso());
                        }}
                      >
                        Data
                      </Button>
                      <Button size="small" color="error" onClick={() => handleDeleteEntry(e)}>Trinti</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Rodoma {filtered.length} įrašų (naujausi viršuje). PDF eksportas rikiuoja seniausius viršuje.
        </Typography>
      </Container>

      <Dialog open={manualOpen} onClose={() => !manualSaving && setManualOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Rankinis žurnalo įrašas</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipas</InputLabel>
              <Select
                label="Tipas"
                value={manualForm.type}
                onChange={(e) => setManualForm((f) => ({ ...f, type: e.target.value }))}
              >
                {Object.entries(ENTRY_TYPE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Data"
              type="date"
              size="small"
              value={manualForm.date}
              onChange={(e) => setManualForm((f) => ({ ...f, date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Dokumento nr."
              size="small"
              value={manualForm.documentNumber}
              onChange={(e) => setManualForm((f) => ({ ...f, documentNumber: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Kontrahentas / klientas"
              size="small"
              value={manualForm.counterpartyName}
              onChange={(e) => setManualForm((f) => ({ ...f, counterpartyName: e.target.value, clientName: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Kliento el. paštas (balansui)"
              size="small"
              value={manualForm.clientEmail}
              onChange={(e) => setManualForm((f) => ({ ...f, clientEmail: e.target.value }))}
              fullWidth
              helperText="Reikalingas pardavimui, mokėjimui, avansui"
            />
            <TextField
              label="Aprašymas"
              size="small"
              value={manualForm.description}
              onChange={(e) => setManualForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Suma Eur"
              size="small"
              type="number"
              value={manualForm.amountEur}
              onChange={(e) => setManualForm((f) => ({ ...f, amountEur: e.target.value }))}
              fullWidth
              inputProps={{ step: '0.01', min: '0' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualOpen(false)} disabled={manualSaving}>Atšaukti</Button>
          <Button variant="contained" onClick={handleManualSave} disabled={manualSaving}>Išsaugoti</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={purchaseOpen} onClose={() => !purchaseSaving && setPurchaseOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Pirkimas / sąnaudos</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              label="Data (tiekėjo SF data)"
              type="date"
              size="small"
              value={purchaseForm.date}
              onChange={(e) => setPurchaseForm((f) => ({ ...f, date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Tiekėjo SF nr."
              size="small"
              value={purchaseForm.documentNumber}
              onChange={(e) => setPurchaseForm((f) => ({ ...f, documentNumber: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Tiekėjas"
              size="small"
              value={purchaseForm.counterpartyName}
              onChange={(e) => setPurchaseForm((f) => ({ ...f, counterpartyName: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Aprašymas"
              size="small"
              value={purchaseForm.description}
              onChange={(e) => setPurchaseForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Suma Eur"
              size="small"
              type="number"
              value={purchaseForm.amountEur}
              onChange={(e) => setPurchaseForm((f) => ({ ...f, amountEur: e.target.value }))}
              fullWidth
              inputProps={{ step: '0.01', min: '0' }}
            />
            <Button variant="outlined" component="label">
              {purchaseForm.file ? purchaseForm.file.name : 'Įkelti SF PDF'}
              <input
                type="file"
                accept="application/pdf"
                hidden
                onChange={(e) => setPurchaseForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
              />
            </Button>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={purchaseForm.paidImmediately}
                  onChange={(e) => setPurchaseForm((f) => ({ ...f, paidImmediately: e.target.checked }))}
                />
              )}
              label="Jau apmokėta (sukurti ir mokėjimo tiekėjui įrašą)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseOpen(false)} disabled={purchaseSaving}>Atšaukti</Button>
          <Button variant="contained" onClick={handlePurchaseSave} disabled={purchaseSaving}>Išsaugoti</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editDateEntry)} onClose={() => !editDateSaving && setEditDateEntry(null)}>
        <DialogTitle>Keisti įrašo datą</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Naudinga, kai „Payed“ pažymėjote ne tą dieną, kai pinigai atėjo į banką.
          </Typography>
          <TextField
            label="Data"
            type="date"
            size="small"
            fullWidth
            value={editDateValue}
            onChange={(e) => setEditDateValue(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDateEntry(null)} disabled={editDateSaving}>Atšaukti</Button>
          <Button variant="contained" onClick={handleSaveEditDate} disabled={editDateSaving}>Išsaugoti</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
